// StationMultiYearSummary.js


import React from "react";
import { useState, useEffect, useContext, useRef } from "react";
import { useFetch } from "use-http";
import { useNavigate } from "react-router-dom";
import { getDBURL, convertDateToDisplayformat, usePopupFocus , } from "../Utils/Utils";
import { UserContext } from "../../App";
import { Box, Button, Text, Layer } from "grommet";
import { Trophy, SubtractCircle, Dislike, RadialSelected, CircleInformation, FastForward} from "grommet-icons";
import { useErrorLogger } from './Logger';
import EvaluationSummary from "./EvaluationSummary";
import SheetStateButton from "./SheetStateButton";
import OpenSummaryButton from "./OpenSummaryButton";
import { Gallery } from "grommet-icons";
import ImageUpload from "./ImageUpload";
import useTrapFocus from "../Utils/useTrapFocus";

const StationMultiYearSummary = (props) => {
    const {station, openTreatment, onClose} = props;
    const currentUser = useContext(UserContext);
    const { DBlogError } = useErrorLogger();
    const { get, post } = useFetch( getDBURL() ); 
    const navigate = useNavigate();

    // console.log('In StationMultiYearSummary props >> ', props);


    const [stationEvaluations, setStationEvaluations] = useState([]);
    const [selectedEvaluation, setSelectedEvaluation] = useState(null);  // set selected one in set of loaded evaluations(preharvest)
    const [showEvaluationSummary, setShowEvaluationSummary] = useState(false);

    const [openUploadImageWindow, setOpenUploadImageWindow] = useState(false);  
    const [uploadParams, setUploadParams] = useState({});
 
    const handleOpenImageUpload = (params) => {
        setUploadParams(params);
        setOpenUploadImageWindow(true);
    };

    // Ref for the modal and focus trap to keep focus within it  
    const modalRef = useRef(null);
    useTrapFocus(modalRef, true); // Trap focus inside the modal while it's open

    // Manage focus when opening modal windows
    usePopupFocus(showEvaluationSummary, "eval-summ");
    usePopupFocus(openUploadImageWindow, "upload-image");

    const [sheetColor, setSheetColor] = useState([]);

    useEffect(() => {
        // get all evaluations for seedling
        (async function loadEvaluationsByStationId() {
            try {
                // console.log("station :  ",  station);
                const response = await get("/loadStationEvaluationSummary/station_id=" + station.Id + "/dt=" + Date.now()); 
                // console.log("evaluations list:  ", res);


                // Check if the response is successful
                if (!response.success) {
                    throw new Error(`HTTP error! ${response.message}`);
                }
        
                const res = response.result;                
                if( res !== undefined && res.length > 0) {
                    setStationEvaluations(res);
                }           
            }
            catch (error) {
                console.log("Error in loadEvaluationsByStationId >> ", error.message);
                await DBlogError({
                    error,
                    parameters: JSON.stringify({ stationName: station.Name, stationId: station.Id }),
                    userName: currentUser.email,
                    moduleName: 'StationMultiYearSummary',
                    functionName: 'loadEvaluationsByStationId',
                });
            } 
        })();
    }, [])

    useEffect(() => {
        if (stationEvaluations.length > 0)
        {
            let temp = [];
            stationEvaluations.map((evaluation) => {                      
                temp.push ({
                    ...evaluation,
                    sheetID: evaluation.EvaluationId,
                    sheetType: evaluation.EvaluationType,
                    treatmentID: evaluation.TreatmentId,
                    treatmentName: evaluation.TreatmentName,
                    treatmentStatus: evaluation.TreatmentStatus,
                    gradeColor: 
                        evaluation.EvaluationGrade === 0
                        ? "#CCCCCC"    // "status-unknown"
                        : (evaluation.EvaluationGrade > 0 && evaluation.EvaluationGrade < 35) 
                            ? "#ffc3a1" 
                            :  (evaluation.EvaluationGrade >= 35 && evaluation.EvaluationGrade < 80) 
                                ? "lightyellow" : "#bed3be",
                    evaluationGrade: evaluation.EvaluationGrade,
                    evaluationYear: evaluation.EvaluationYear,
                    evaluationImg: evaluation.BunchImgExist,
                    sheetColor:
                        evaluation.EvaluationType === 2
                        ? "#ffc3a1"
                        : "#bed3be",
                    sheetName:
                        evaluation.EvaluationCreateDate !== null ? convertDateToDisplayformat(evaluation.EvaluationCreateDate) : "new evaluation",  // only first evaluation for the seedling
                    sheetFontColor:
                        evaluation.EvaluationType === 2
                        ? "#f55d07"
                        : "#436343",                                                            
                })                      
            });
            // console.log("evaluations buttons:  ", temp);
            setSheetColor(temp);
        }
    }, [stationEvaluations]);

    const style = {
        border: "2px solid",
        margin: "10px",
    };

    return (
        <Layer
        id="danger-msg"
        position="center"
        style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dim background for modal effect
        }}              
        onEsc={onClose}
        onClickOutside={() => {}}
    >
        <Box
            id="stat-summ"
            ref={modalRef}
            tabIndex="-1"
            overflow='auto'
            pad="xxsmall" 
            gap="small" 
            // width="medium" 
            width="460px"
            background="white"  
            
            style={{
                position: 'relative',
                margin: 'auto', // Center horizontally and vertically
            }}
        >             
            <Box  style={style}  background="light-3" pad="small"  >

                <Box style={{ padding: "10px 10px 0 10px"}} align="center">
                    <Text
                    weight="bold"
                    style={{
                        background: "linear-gradient(to left, #00739D, #f0869c 100%)",
                        backgroundPosition: "0 100%",
                        backgroundSize: "100% 2px",
                        backgroundRepeat: "repeat-x",
                        fontSize: "26px",
                        fontStyle: "italic",
                        lineHeight: "1.5", // Adjust this to control the spacing
                        paddingBottom: "5px" // Or use this for direct control
                    }}
                    >
                    Multi-Year Summary
                    </Text>
                </Box>

                {/* list of evaluations buttons */}
                <Box align="center" margin={{vertical: "small"}} >
                    <Box pad={{vertical: "xxsmall"}} >
                        {sheetColor.length === 0 && 
                        <Text> No data available</Text>
                        }

                        {sheetColor.length > 0 && sheetColor.map (el => 
                        <Box align="center" 
                            direction="row-responsive"
                            key={(el.sheetID)}
                            >


                            <Box direction="row">
                                <Text weight="bold" style={{fontStyle: "italic"}} >{el.evaluationYear}&nbsp;&nbsp;</Text>
                                <Box width= "30px"  margin="auto">
                                    <FastForward  color="brand" size='15px' />                                
                                </Box>                 
                            </Box>


                            {/* {console.log('el ==>>  ', el)} */}
                            <Box pad="xxsmall" width= "200px" height="30px" >
                                <Button 
                                    color={"#33333310"}
                                    style={{color: "#047343", padding: 0, fontSize: "18px"}}                                    
                                    primary                            
                                    label={el.treatmentName}
                                    onClick={() => {
                                        // console.log('el.TreatmentId >>>    ', el.TreatmentId);
                                        if (el.TreatmentId !== null && el.TreatmentId !== '00000000-0000-0000-0000-000000000000') 
                                            openTreatment(el)
                                        } }
                                    />
                            </Box>                             

                            {(el.EvaluationType !== 2 && el.evaluationYear !== null) ? (
                            <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" width="50px"> 
                                <OpenSummaryButton 
                                    evaluationMaxGrade={el.evaluationGrade} el={el} 
                                    showEvaluationSummary = {setShowEvaluationSummary} 
                                    setSelectedEvaluation = {setSelectedEvaluation} 
                                    />                        
                                                          
                            </Box> 
                            ) :

                            (el.EvaluationType === 2) &&
                            <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" width="50px" margin={{horizontal: "xxsmall"}}>
                             </Box>
                            }

                            {(el.evaluationImg === 1) &&
                                <Button
                                    style={{ color: "#ffffff", textAlign: "center", padding: "6px" }}
                                    icon={<Gallery color='white' />}
                                    primary
                                    onClick={() => handleOpenImageUpload({
                                        docType: "Evaluation",
                                        docId: el.sheetID,
                                        imgType: "Bunch",
                                        stateHandler: null,
                                        initValue: el.evaluationImg,
                                        fieldName: "BunchImgExist",
                                        tableName: "VEM_Evaluations",
                                        disable: true
                                    })}
                                />                               
                            }
                        </Box>                                           
                        )}

                        {showEvaluationSummary && 
                         <Box>
                            <EvaluationSummary plant={null} station={null} treatment={null} 
                                setShowEvaluationSummary={setShowEvaluationSummary}
                                evaluation={(selectedEvaluation.sheetID)}
                            />
                        </Box> 
                        }

                        {openUploadImageWindow && 
                        <Box>
                            <ImageUpload 
                                onClose={setOpenUploadImageWindow} 
                                {...uploadParams}                           
                            />
                        </Box>}                        

                    </Box>
                </Box>
                {/* end of buttons of evaluations */}

                <Box align="center" justify="center" margin="medium">
                    <Button
                    label={
                        <Text >
                        <strong>Close</strong>
                        </Text>
                    }
                    style={{padding: "0px", width: "150px"}}
                    onClick={onClose}
                    secondary                   
                    />
                </Box>
            </Box>
        </Box>
        </Layer>
    );
};

export default StationMultiYearSummary;
